import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/** The lead page's side sheet — the web twin of the app's bottom sheets.
 *  Right-hand slide-over (same place as the chat), forest or gold header,
 *  scrolling body, footer for the verbs. Esc and the backdrop close it. */
const Sheet: React.FC<{
  title: string;
  sub?: string;
  tone?: 'forest' | 'gold';
  onClose: () => void;
  footer?: React.ReactNode;
  error?: string | null;
  children: React.ReactNode;
}> = ({ title, sub, tone = 'forest', onClose, footer, error, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-charcoal/30" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-warm-white border-l-[1.5px] border-sketch shadow-sketch-lg flex flex-col">
        <div className={`${tone === 'gold' ? 'bg-gold text-charcoal' : 'bg-forest text-warm-white'} px-5 py-3.5 flex items-center justify-between`}>
          <div className="min-w-0">
            <h2 className="font-display text-[17px] font-semibold m-0 truncate">{title}</h2>
            {sub && <p className="text-[12px] opacity-80 m-0 truncate">{sub}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" className="opacity-90 hover:opacity-100">
            <X className="w-5 h-5" strokeWidth={1.9} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {children}
          {error && <p className="text-sm text-brick">{error}</p>}
        </div>
        {footer && <div className="px-5 py-3 border-t-[1.5px] border-sketch flex gap-2 justify-end flex-wrap">{footer}</div>}
      </div>
    </div>
  );
};

export const SheetLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="font-display text-[11px] font-semibold tracking-[0.09em] uppercase text-graphite mb-1.5">{children}</div>
);

/** A card whose body folds away; the header keeps its counter when shut. */
export const FoldCard: React.FC<{
  title: string;
  icon?: React.ReactNode;
  count?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, count, defaultOpen = true, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="card">
      <button type="button" className="card-h w-full !mb-0 text-left" aria-expanded={open} onClick={() => setOpen(!open)}>
        {icon}{title}
        {count != null && <span className="ml-auto font-sans normal-case tracking-normal text-[11.5px] text-amber-ink">{count}</span>}
        <span className={`${count != null ? 'ml-2' : 'ml-auto'} text-graphite transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
};

/** Pull a readable message off an axios error. */
export const errText = (e: any): string => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d) return JSON.stringify(d);
  return e?.message || 'Something went wrong';
};

export default Sheet;
